/**
 * StatusMonitor — Status monitoring notification system
 *
 * Common across all Provider categories (IDE/Extension/CLI/ACP).
 * - Approval waiting (waiting_approval) notification
 * - No-progress watchdog: alert when an active turn makes no progress for a while
 * - All config toggleable via Provider Settings
 */

export interface MonitorConfig {
 /** Enable awaiting-approval notification */
    approvalAlert: boolean;
 /** No-progress watchdog notification enabled */
    noProgressAlert: boolean;
 /** No-progress threshold (seconds) */
    noProgressThresholdSec: number;
 /** Repeat notification cooldown (seconds) */
    alertCooldownSec: number;
}

export const DEFAULT_MONITOR_CONFIG: MonitorConfig = {
    approvalAlert: true,
    noProgressAlert: true,
    noProgressThresholdSec: 180,   // 3 minutes
    alertCooldownSec: 60,          // 1 minute cooldown
};

export interface MonitorEvent {
    type: string;
    agentKey: string;
    timestamp: number;
    elapsedSec?: number;
    message?: string;
}

export class StatusMonitor {
    private config: MonitorConfig;
    private lastAlertTime = new Map<string, number>();
    private generatingStartTimes = new Map<string, number>();
    private noProgressAlerted = new Map<string, boolean>();
    private lastProgressFingerprint = new Map<string, string>();
    private lastProgressChangeAt = new Map<string, number>();

    constructor(config?: Partial<MonitorConfig>) {
        this.config = { ...DEFAULT_MONITOR_CONFIG, ...config };
    }

 /** Update config (called from Provider Settings) */
    updateConfig(partial: Partial<MonitorConfig>): void {
        Object.assign(this.config, partial);
    }

 /** Return current config */
    getConfig(): MonitorConfig {
        return { ...this.config };
    }

 /**
  * Check status transition → return notification event array.
  * Called from each onTick() or detectStatusTransition().
  */
    check(agentKey: string, status: string, now: number, progressFingerprint?: string, approvalPending?: boolean): MonitorEvent[] {
        const events: MonitorEvent[] = [];

 // 1. Approval waiting notification
        if (this.config.approvalAlert && status === 'waiting_approval') {
            if (this.shouldAlert(agentKey + ':approval', now)) {
                events.push({
                    type: 'monitor:approval_waiting',
                    agentKey,
                    timestamp: now,
                    message: `${agentKey} is waiting for approval`,
                });
            }
        }

 // 2. No-progress watchdog (identical for IDE/Extension/CLI/ACP)
        if (status === 'generating' || status === 'streaming') {
            // While an approval modal is pending we must NOT count the wait as
            // no-progress. The assistant emits no tokens and the screen is frozen
            // on the modal, so the progress fingerprint goes static — but this is
            // a user action wait, not a freeze. Some callers (CLI/IDE auto-approve)
            // also synthesize the reported status to 'generating' during the wait,
            // so the plain `waiting_approval` branch below cannot catch it. Hold the
            // progress anchor at `now` so the elapsed timer restarts the moment the
            // approval clears and real generating resumes.
            if (approvalPending) {
                this.generatingStartTimes.set(agentKey, now);
                this.lastProgressFingerprint.set(agentKey, progressFingerprint ?? '');
                this.lastProgressChangeAt.set(agentKey, now);
                this.noProgressAlerted.set(agentKey, false);
                return events;
            }
            if (!this.generatingStartTimes.has(agentKey)) {
                this.generatingStartTimes.set(agentKey, now);
                this.noProgressAlerted.set(agentKey, false);
                const initialFingerprint = progressFingerprint ?? '';
                this.lastProgressFingerprint.set(agentKey, initialFingerprint);
                this.lastProgressChangeAt.set(agentKey, now);
            }
            const currentFingerprint = progressFingerprint ?? '';
            const previousFingerprint = this.lastProgressFingerprint.get(agentKey);
            if (previousFingerprint !== currentFingerprint) {
                this.lastProgressFingerprint.set(agentKey, currentFingerprint);
                this.lastProgressChangeAt.set(agentKey, now);
                this.noProgressAlerted.set(agentKey, false);
            }
            if (this.config.noProgressAlert) {
                const progressChangedAt = this.lastProgressChangeAt.get(agentKey) || this.generatingStartTimes.get(agentKey)!;
                const elapsedSec = Math.round((now - progressChangedAt) / 1000);
                const alreadyAlerted = this.noProgressAlerted.get(agentKey) === true;
                if (elapsedSec > this.config.noProgressThresholdSec && !alreadyAlerted) {
                    if (this.shouldAlert(agentKey + ':no_progress', now)) {
                        this.noProgressAlerted.set(agentKey, true);
                        events.push({
                            type: 'monitor:no_progress',
                            agentKey,
                            elapsedSec,
                            timestamp: now,
                            message: `${agentKey} output appears stuck for ${Math.round(elapsedSec / 60)}min`,
                        });
                    }
                }
            }
        } else {
 // Reset timer when switching to non-generating status
            this.generatingStartTimes.delete(agentKey);
            this.noProgressAlerted.delete(agentKey);
            this.lastProgressFingerprint.delete(agentKey);
            this.lastProgressChangeAt.delete(agentKey);
        }

        return events;
    }

 /** Cooldown check — prevent sending the same notification too frequently */
    private shouldAlert(key: string, now: number): boolean {
        const last = this.lastAlertTime.get(key) || 0;
        if (now - last > this.config.alertCooldownSec * 1000) {
            this.lastAlertTime.set(key, now);
            return true;
        }
        return false;
    }

 /** Reset (on agent terminate/restart) */
    reset(agentKey?: string): void {
        if (agentKey) {
            this.generatingStartTimes.delete(agentKey);
            this.noProgressAlerted.delete(agentKey);
            this.lastProgressFingerprint.delete(agentKey);
            this.lastProgressChangeAt.delete(agentKey);
 // Delete all cooldowns for this key
            for (const k of this.lastAlertTime.keys()) {
                if (k.startsWith(agentKey)) this.lastAlertTime.delete(k);
            }
        } else {
            this.generatingStartTimes.clear();
            this.noProgressAlerted.clear();
            this.lastProgressFingerprint.clear();
            this.lastProgressChangeAt.clear();
            this.lastAlertTime.clear();
        }
    }
}
